// Renders a branded gold-on-indigo Adinkra card to a PNG blob, then shares
// (native file share where supported) or downloads it. Used by the leak
// diagnostic result and the course-completion moment.
const BRAND_URL = "course.oxytocin-learning.com";
const GOLD = "#D4AF37";

async function loadTintedSymbol(symbol, color, size) {
  const res = await fetch(`/adinkra/${symbol}.svg`);
  let txt = await res.text();
  if (!/\swidth=/.test(txt)) txt = txt.replace("<svg", `<svg width="${size}" height="${size}"`);
  const url = URL.createObjectURL(new Blob([txt], { type: "image/svg+xml" }));
  try {
    const img = new Image();
    await new Promise((ok, err) => { img.onload = ok; img.onerror = err; img.src = url; });
    const off = document.createElement("canvas");
    off.width = size; off.height = size;
    const c = off.getContext("2d");
    c.drawImage(img, 0, 0, size, size);
    c.globalCompositeOperation = "source-in";
    c.fillStyle = color;
    c.fillRect(0, 0, size, size);
    return off;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function drawSpaced(ctx, text, cx, y, spacing, font, color) {
  ctx.save();
  ctx.font = font;
  ctx.fillStyle = color;
  ctx.textAlign = "left";
  const chars = [...text];
  const widths = chars.map((ch) => ctx.measureText(ch).width + spacing);
  const total = widths.reduce((a, b) => a + b, 0) - spacing;
  let x = cx - total / 2;
  chars.forEach((ch, i) => { ctx.fillText(ch, x, y); x += widths[i]; });
  ctx.restore();
}

function wrapText(ctx, text, cx, y, maxW, lh, font, color) {
  ctx.save();
  ctx.font = font;
  ctx.fillStyle = color;
  ctx.textAlign = "center";
  const words = text.split(" ");
  const lines = [];
  let line = "";
  for (const w of words) {
    const t = line ? `${line} ${w}` : w;
    if (ctx.measureText(t).width > maxW && line) { lines.push(line); line = w; }
    else line = t;
  }
  if (line) lines.push(line);
  lines.forEach((l, i) => ctx.fillText(l, cx, y + i * lh));
  ctx.restore();
  return y + lines.length * lh;
}

export async function renderAdinkraCard({ symbol, kicker, title, subtitle, footer = BRAND_URL, awardName, dateStr }) {
  const W = 1080, H = 1350;
  const cv = document.createElement("canvas");
  cv.width = W; cv.height = H;
  const ctx = cv.getContext("2d");

  const g = ctx.createLinearGradient(0, 0, W, H);
  g.addColorStop(0, "#2A1A5E"); g.addColorStop(0.55, "#221452"); g.addColorStop(1, "#160C36");
  ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);

  const rg = ctx.createRadialGradient(W / 2, 430, 30, W / 2, 430, 640);
  rg.addColorStop(0, "rgba(212,175,55,0.26)"); rg.addColorStop(1, "rgba(212,175,55,0)");
  ctx.fillStyle = rg; ctx.fillRect(0, 0, W, H);

  ctx.strokeStyle = "rgba(212,175,55,0.5)"; ctx.lineWidth = 3;
  ctx.strokeRect(46, 46, W - 92, H - 92);

  drawSpaced(ctx, "OXYTOCIN · LEARNING", W / 2, 150, 6, "bold 26px Arial, sans-serif", "rgba(232,212,139,0.85)");

  const symSize = 300;
  const sym = await loadTintedSymbol(symbol, GOLD, symSize);
  ctx.drawImage(sym, (W - symSize) / 2, 240, symSize, symSize);

  drawSpaced(ctx, kicker.toUpperCase(), W / 2, 636, 5, "bold 27px Arial, sans-serif", GOLD);

  let y = wrapText(ctx, title, W / 2, 730, W - 240, 86, "italic 72px Georgia, serif", "#FFFFFF");
  if (subtitle) y = wrapText(ctx, subtitle, W / 2, y + 44, W - 260, 48, "34px Georgia, serif", "rgba(245,243,252,0.78)");

  // Certificate block — turns the card into a personal, postable certificate
  if (awardName) {
    const ry = y + 70;
    ctx.strokeStyle = "rgba(212,175,55,0.6)"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(W / 2 - 90, ry); ctx.lineTo(W / 2 + 90, ry); ctx.stroke();
    drawSpaced(ctx, "AWARDED TO", W / 2, ry + 52, 6, "bold 24px Arial, sans-serif", "rgba(232,212,139,0.85)");
    ctx.textAlign = "center";
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "50px Georgia, serif";
    ctx.fillText(awardName, W / 2, ry + 116);
    if (dateStr) {
      ctx.fillStyle = "rgba(245,243,252,0.6)";
      ctx.font = "26px Georgia, serif";
      ctx.fillText(dateStr, W / 2, ry + 158);
    }
  }

  ctx.textAlign = "center";
  ctx.fillStyle = "rgba(232,212,139,0.9)";
  ctx.font = "bold 30px Arial, sans-serif";
  ctx.fillText(footer, W / 2, H - 110);
  ctx.fillStyle = "rgba(245,243,252,0.45)";
  ctx.font = "24px Georgia, serif";
  ctx.fillText("How Truth Travels", W / 2, H - 70);

  return await new Promise((resolve) => cv.toBlob((b) => resolve(b), "image/png"));
}

export async function shareOrDownload(blob, filename, shareData = {}) {
  const file = new File([blob], filename, { type: "image/png" });
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: shareData.title, text: shareData.text });
      return "shared";
    } catch (e) {
      if (e && e.name === "AbortError") return "aborted";
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  return "downloaded";
}
